import { Chevron } from "components/blocks/clinicTeam"
import BioModal from "components/blocks/clinicTeam/bioModal"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { optimizeContentfulImage } from "utils/optimizeContentfulImage"
import { renderMarkdownParagraphs } from "utils/inlineMarkdown"
import "./staff-cards-v2.scss"

// Ordered List display style "Featured Cards", first used on /about: large
// cards with a photo and short bio (the co-founders). The "Card Grid" style
// uses the locations pages' team cards instead (components/blocks/clinicTeam).

const photoUrl = (member, size) =>
  member.picture?.file?.url &&
  optimizeContentfulImage(`https:${member.picture.file.url}`, {
    width: size,
    height: size,
    fit: "thumb",
    focus: "face",
  })

const Header = ({ intro, title }) => (
  <>
    {title && <h2 className="staff-cards-v2--title">{title}</h2>}
    {intro && <p className="staff-cards-v2--intro">{intro}</p>}
  </>
)

Header.propTypes = { intro: PropTypes.string, title: PropTypes.string }

const roleText = role => role?.replace(/\s*\|\s*/g, " and ")

// "See More" goes to the person's own page when Profile page is set (for
// example Dr. Goh's page), and otherwise opens the same bio pop-up as the
// locations team cards. With neither, there is no link.
const SeeMore = ({ member, onOpen }) => {
  if (member.profilePage) {
    return (
      <a
        className="ClinicTeam__more staff-cards-v2--more"
        href={member.profilePage}
      >
        See More
        <span className="sr-only"> about {member.name}</span>
        <Chevron />
      </a>
    )
  }
  if (!member.details?.json) return null
  return (
    <button
      aria-haspopup="dialog"
      className="ClinicTeam__more staff-cards-v2--more"
      onClick={onOpen}
      type="button"
    >
      See More
      <span className="sr-only"> about {member.name}</span>
      <Chevron />
    </button>
  )
}

SeeMore.propTypes = {
  member: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
}

export const FeaturedStaffCards = ({ intro, members, title }) => {
  const [openIndex, setOpenIndex] = useState(null)
  const open = openIndex === null ? null : members[openIndex]

  return (
    <section className="staff-cards-v2 staff-cards-v2--featured">
      <div className="container-v2">
        <Header intro={intro} title={title} />
        <ul className="staff-cards-v2--featured-list">
          {members.map((member, index) => (
            <li className="staff-cards-v2--featured-card" key={member.id}>
              {member.picture?.file?.url && (
                <img
                  alt={member.name}
                  className="staff-cards-v2--featured-photo"
                  loading="lazy"
                  src={photoUrl(member, 480)}
                />
              )}
              <div>
                <h3 className="staff-cards-v2--name">{member.name}</h3>
                {member.role && (
                  <p className="staff-cards-v2--role">
                    {roleText(member.role)}
                  </p>
                )}
                {member.shortBio?.shortBio && (
                  <div className="staff-cards-v2--bio">
                    {renderMarkdownParagraphs(member.shortBio.shortBio)}
                  </div>
                )}
                <SeeMore member={member} onOpen={() => setOpenIndex(index)} />
              </div>
            </li>
          ))}
        </ul>
      </div>

      {open && (
        <BioModal
          doctor={{ ...open, role: roleText(open.role) }}
          onClose={() => setOpenIndex(null)}
          tone="blue"
        />
      )}
    </section>
  )
}

FeaturedStaffCards.propTypes = {
  intro: PropTypes.string,
  members: PropTypes.array.isRequired,
  title: PropTypes.string,
}
